import { Room, RoomEvent, LocalAudioTrack } from "livekit-client";

const LS_KEY = "gn_conn_test";
const TTL_AUDIO_MODE_MS = 1 * 60 * 60 * 1000;   // 1 hour when audio-only is recommended
const TTL_DEFAULT_MS = 24 * 60 * 60 * 1000;      // 24 hours otherwise

interface CachedResult {
  ip: string;
  testedAt: number;
  recommendAudioOnly: boolean;
}

// Singleton: if a test is already running, callers share the same promise
// rather than launching a parallel test with an identical LiveKit identity.
let _pendingTest: Promise<boolean> | null = null;

export async function checkConnectionQuality(
  callAPI: Function,
  authToken: string
): Promise<boolean> {
  if (_pendingTest) return _pendingTest;
  _pendingTest = _runTest(callAPI, authToken).finally(() => { _pendingTest = null; });
  return _pendingTest;
}

async function getCurrentIp(): Promise<string | null> {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") return "127.0.0.1";
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const json = await res.json();
    return json.ip ?? null;
  } catch {
    return null;
  }
}

async function _runTest(
  callAPI: Function,
  authToken: string
): Promise<boolean> {
  console.log("[ConnectionTest] Checking connection quality...");

  const cached: CachedResult | null = parseCache();
  const age = cached ? Date.now() - cached.testedAt : Infinity;
  const ttl = cached?.recommendAudioOnly ? TTL_AUDIO_MODE_MS : TTL_DEFAULT_MS;
  if (cached && age < ttl) {
    const currentIp = await getCurrentIp();
    if (!currentIp || currentIp === cached.ip) {
      console.log(`[ConnectionTest] Using cached result (age ${Math.round(age / 60_000)}m): recommendAudioOnly=${cached.recommendAudioOnly}`);
      return cached.recommendAudioOnly;
    }
    console.log(`[ConnectionTest] Cache invalid — IP changed (was ${cached.ip}, now ${currentIp}), running fresh test`);
  } else {
    console.log(cached ? `[ConnectionTest] Cache expired (age ${Math.round(age / 60_000)}m), running fresh test` : "[ConnectionTest] No cached result, running fresh test");
  }

  const data = await callAPI("test_connection", { method: "GET", token: authToken });
  if (!data?.token) return false;

  console.log("[ConnectionTest] Starting LiveKit connection quality test...");

  const room = new Room();
  let audioCtx: AudioContext | null = null;
  let resolved = false;
  let latestQuality = "unknown";
  let qualityTimer: ReturnType<typeof setTimeout> | null = null;

  const recommendAudioOnly = await new Promise<boolean>((resolve) => {
    const done = (result: boolean) => {
      if (resolved) return;
      resolved = true;
      if (qualityTimer) clearTimeout(qualityTimer);
      clearTimeout(timeout);
      resolve(result);
    };

    // Overall safety net — connection time + 10s window, plus a small buffer
    const timeout = setTimeout(() => {
      done(latestQuality === "poor" || latestQuality === "lost");
    }, 18_000);

    room.on(RoomEvent.ConnectionQualityChanged, (quality: string) => {
      console.log(`[ConnectionTest] Connection quality: ${quality}`);
      latestQuality = quality;
      if (quality === "poor" || quality === "lost") done(true);
    });

    room.on(RoomEvent.Disconnected, (reason: any) => {
      if (reason === 1) return; // CLIENT_INITIATED — our own disconnect call
      done(true);
    });

    room
      .connect(data.livekit_url, data.token)
      .then(async () => {
        try {
          // Create a silent audio track via Web Audio API (no mic permission needed).
          // Publishing gives LiveKit real RTP media to measure packet loss/jitter on.
          audioCtx = new AudioContext();
          const oscillator = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          gain.gain.value = 0;
          const dest = audioCtx.createMediaStreamDestination();
          oscillator.connect(gain);
          gain.connect(dest);
          oscillator.start();
          const silentTrack = new LocalAudioTrack(dest.stream.getAudioTracks()[0]);
          await room.localParticipant.publishTrack(silentTrack);
        } catch (err: any) {
          // Silent track publish failed — quality measurement will be less accurate
        }
        qualityTimer = setTimeout(() => {
          done(latestQuality === "poor" || latestQuality === "lost");
        }, 10_000);
      })
      .catch(() => done(true));
  });

  await room.disconnect().catch(() => {});
  audioCtx?.close().catch(() => {});

  console.log(`[ConnectionTest] Test complete — quality: ${latestQuality}, recommendAudioOnly: ${recommendAudioOnly}`);

  localStorage.setItem(
    LS_KEY,
    JSON.stringify({ ip: data.ip_address, testedAt: Date.now(), recommendAudioOnly })
  );

  return recommendAudioOnly;
}

export function getCachedAudioOnly(): boolean {
  return parseCache()?.recommendAudioOnly ?? false;
}

function parseCache(): CachedResult | null {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "null");
  } catch {
    localStorage.removeItem(LS_KEY);
    return null;
  }
}
